import {
  BloomsTaxonomy,
  DepthOfKnowledge,
  GradeLevel,
  ResourceMaturity,
  ResourceVisibilityType,
  ScoringMethodEnum,
  Subject,
} from "@examspark/types";

export * from "./tasks";

export const subjectMeta = {
  [Subject.CareerTechnicalEducation]: {
    label: "Career & Technical Education",
  },
  [Subject.EnglishLanguageArts]: {
    label: "English-Language Arts",
  },
  [Subject.SpanishLanguageArts]: {
    label: "Spanish-Language Arts",
  },
  [Subject.Mathematics]: {
    label: "Mathematics",
  },
  [Subject.Science]: {
    label: "Science",
  },
  [Subject.SocialStudies]: {
    label: "Social Studies",
  },
  [Subject.MultiSubject]: {
    label: "Pre-K (Multi-Subject)",
  },
};

export const subjectList = [
  Subject.EnglishLanguageArts,
  Subject.SpanishLanguageArts,
  Subject.Mathematics,
  Subject.Science,
  Subject.SocialStudies,
  Subject.CareerTechnicalEducation,
  Subject.MultiSubject,
];

export const scoringMethodMeta = {
  [ScoringMethodEnum.PerQuestion]: {
    label: "Per Question",
    value: ScoringMethodEnum.PerQuestion,
  },
  [ScoringMethodEnum.Dichotomous]: {
    label: "Dichotomous",
    value: ScoringMethodEnum.Dichotomous,
  },
  [ScoringMethodEnum.Dependent]: {
    label: "Dependent (EBSR)",
    value: ScoringMethodEnum.Dependent,
  },
  [ScoringMethodEnum.CustomTxStaar]: {
    label: "Texas STAAR Assessment",
    value: ScoringMethodEnum.CustomTxStaar,
  },
  [ScoringMethodEnum.CustomLimitedPartialCredit]: {
    label: "Limited Partial Credit",
    value: ScoringMethodEnum.CustomLimitedPartialCredit,
  },
};

export const scoringMethodList = [
  ScoringMethodEnum.PerQuestion,
  ScoringMethodEnum.Dichotomous,
  ScoringMethodEnum.Dependent,
  ScoringMethodEnum.CustomTxStaar,
  ScoringMethodEnum.CustomLimitedPartialCredit,
];

export const gradeLevelList = Object.values(GradeLevel) as Array<GradeLevel>;

export const gradeLevelMeta = {
  [GradeLevel.PreKindergarten]: {
    label: "Pre-K",
  },
  [GradeLevel.Kindergarten]: {
    label: "Kindergarten",
  },
  [GradeLevel.Grade1]: {
    label: "Grade 1",
  },
  [GradeLevel.Grade2]: {
    label: "Grade 2",
  },
  [GradeLevel.Grade3]: {
    label: "Grade 3",
  },
  [GradeLevel.Grade4]: {
    label: "Grade 4",
  },
  [GradeLevel.Grade5]: {
    label: "Grade 5",
  },
  [GradeLevel.Grade6]: {
    label: "Grade 6",
  },
  [GradeLevel.Grade7]: {
    label: "Grade 7",
  },
  [GradeLevel.Grade8]: {
    label: "Grade 8",
  },
  [GradeLevel.Grade9]: {
    label: "Grade 9",
  },
  [GradeLevel.Grade10]: {
    label: "Grade 10",
  },
  [GradeLevel.Grade11]: {
    label: "Grade 11",
  },
  [GradeLevel.Grade12]: {
    label: "Grade 12",
  },
  [GradeLevel.HighSchool]: {
    label: "High School",
  },
};

export const resourceMaturityList = [
  ResourceMaturity.Draft,
  ResourceMaturity.InDevelopment,
  ResourceMaturity.ArtworkPending,
  ResourceMaturity.ArtworkInProgress,
  ResourceMaturity.ArtworkFeedback,
  ResourceMaturity.ArtworkComplete,
  ResourceMaturity.Feedback,
  // ResourceMaturity.QualityTestingPending,
  // ResourceMaturity.QualityTestingInProgress,
  // ResourceMaturity.QualityTestingFailed,
  // ResourceMaturity.QualityTestingComplete,
  ResourceMaturity.BlockedPreview,
  ResourceMaturity.FinalEdits,
  ResourceMaturity.FinalReview,
  ResourceMaturity.Piloting,
  ResourceMaturity.PreOperational,
  ResourceMaturity.Operational,
  ResourceMaturity.Retired,
  ResourceMaturity.Rejected,
];

export const resourceMaturityMeta: Partial<
  Record<ResourceMaturity, { label: string; value: ResourceMaturity }>
> = {
  [ResourceMaturity.Draft]: {
    label: "Draft",
    value: ResourceMaturity.Draft,
  },
  [ResourceMaturity.InDevelopment]: {
    label: "In Development",
    value: ResourceMaturity.InDevelopment,
  },
  [ResourceMaturity.ArtworkPending]: {
    label: "Artwork: Pending",
    value: ResourceMaturity.ArtworkPending,
  },
  [ResourceMaturity.ArtworkInProgress]: {
    label: "Artwork: In Progress",
    value: ResourceMaturity.ArtworkInProgress,
  },
  [ResourceMaturity.ArtworkFeedback]: {
    label: "Artwork: Feedback",
    value: ResourceMaturity.ArtworkFeedback,
  },
  [ResourceMaturity.ArtworkComplete]: {
    label: "Artwork: Complete",
    value: ResourceMaturity.ArtworkComplete,
  },
  [ResourceMaturity.Feedback]: {
    label: "Feedback",
    value: ResourceMaturity.Feedback,
  },
  [ResourceMaturity.QualityTestingPending]: {
    label: "User Testing: Pending",
    value: ResourceMaturity.QualityTestingPending,
  },
  [ResourceMaturity.QualityTestingInProgress]: {
    label: "User Testing: In Progress",
    value: ResourceMaturity.QualityTestingInProgress,
  },
  [ResourceMaturity.QualityTestingFailed]: {
    label: "User Testing: Failed",
    value: ResourceMaturity.QualityTestingFailed,
  },
  [ResourceMaturity.QualityTestingComplete]: {
    label: "User Testing: Complete",
    value: ResourceMaturity.QualityTestingComplete,
  },
  [ResourceMaturity.BlockedPreview]: {
    label: "Blocked: Preview/Scoring Issues",
    value: ResourceMaturity.BlockedPreview,
  },
  [ResourceMaturity.FinalEdits]: {
    label: "Final Edits",
    value: ResourceMaturity.FinalEdits,
  },
  [ResourceMaturity.Piloting]: {
    label: "Piloting",
    value: ResourceMaturity.Piloting,
  },
  [ResourceMaturity.FinalReview]: {
    label: "Final Review",
    value: ResourceMaturity.FinalReview,
  },
  [ResourceMaturity.PreOperational]: {
    label: "Pre-Operational",
    value: ResourceMaturity.PreOperational,
  },
  [ResourceMaturity.Operational]: {
    label: "Operational",
    value: ResourceMaturity.Operational,
  },
  [ResourceMaturity.Retired]: {
    label: "Retired",
    value: ResourceMaturity.Retired,
  },
  [ResourceMaturity.Rejected]: {
    label: "Rejected",
    value: ResourceMaturity.Rejected,
  },
};

export const resourceVisibilityList = [
  ResourceVisibilityType.Private,
  ResourceVisibilityType.Workspace,
  ResourceVisibilityType.WorkspaceShareable,
];

export const resourceVisibilityMeta: Record<
  ResourceVisibilityType,
  { label: string; value: ResourceVisibilityType }
> = {
  [ResourceVisibilityType.Private]: {
    label: "Private",
    value: ResourceVisibilityType.Private,
  },
  [ResourceVisibilityType.Workspace]: {
    label: "Workspace",
    value: ResourceVisibilityType.Workspace,
  },
  [ResourceVisibilityType.WorkspaceShareable]: {
    label: "Workspace And Shareable",
    value: ResourceVisibilityType.WorkspaceShareable,
  },
};

export const dokList = [
  DepthOfKnowledge.DOK1,
  DepthOfKnowledge.DOK2,
  DepthOfKnowledge.DOK3,
  DepthOfKnowledge.DOK4,
];

export const dokMeta = {
  [DepthOfKnowledge.DOK1]: {
    short: "1",
    label: "1: Recall",
  },
  [DepthOfKnowledge.DOK2]: {
    short: "2",
    label: "2: Skill/Concept",
  },
  [DepthOfKnowledge.DOK3]: {
    short: "3",
    label: "3: Strategic Thinking",
  },
  [DepthOfKnowledge.DOK4]: {
    short: "4",
    label: "4: Extended Thinking",
  },
};

export const bloomsList = [
  BloomsTaxonomy.BT1,
  BloomsTaxonomy.BT2,
  BloomsTaxonomy.BT3,
  BloomsTaxonomy.BT4,
  BloomsTaxonomy.BT5,
  BloomsTaxonomy.BT6,
];

export const bloomsMeta = {
  [BloomsTaxonomy.BT1]: {
    short: "1",
    label: "1: Remember",
  },
  [BloomsTaxonomy.BT2]: {
    short: "2",
    label: "2: Understand",
  },
  [BloomsTaxonomy.BT3]: {
    short: "3",
    label: "3: Apply",
  },
  [BloomsTaxonomy.BT4]: {
    short: "4",
    label: "4: Analyze",
  },
  [BloomsTaxonomy.BT5]: {
    short: "5",
    label: "5: Evaluate",
  },
  [BloomsTaxonomy.BT6]: {
    short: "6",
    label: "6: Create",
  },
};
